import type { StaffOperationsControlReason } from "@lafayette/schemas";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useWideLayout } from "../../layout/shell-layout";
import { AreaLinks } from "../../operations/components/parts";
import { formatInstant } from "../../operations/format";
import { controlReasonLabels } from "../../operations/labels";
import { utcInputToIso } from "../../team/format";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { DataTable } from "../../ui/data-table";
import { SelectField, TextField } from "../../ui/fields";
import { Icon } from "../../ui/icon";
import { PageHeader } from "../../ui/page-header";
import { wrapCells } from "../../ui/wrap-cells";
import { useDiagnosticsCapabilities } from "../access";
import { RecordLink } from "../components/RecordLink";
import { describeDiagnosticsError } from "../errors";
import { auditActorKindLabels, auditFamilies, resolutionLabels } from "../labels";
import { type AuditFilter, emptyAuditFilter, useAuditEvents } from "../queries";
import { runbookPath, runbooks } from "../runbooks";

// The audit trail page (CHT-772; supervisor and administrator). It answers: who did what to which record, when, and why. Rows come from the
// append-only audit log through `GET /staff/audit/events`: an actor (with the name of a staff actor), an action, the record it was about, the
// staff reason, the time and the correlation id. The page holds no message text, no memory value, no note and no `before` or `after`
// metadata: the API sends none. The filter is the actor, the action or the family of actions, the record, and a time window in UTC. The list
// is read page by page ("Load more"), newest first. A record opens only for a role that may open that screen.

const ACTION = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/;
const RECORD_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const RECORD_TYPE = /^[a-z][a-z0-9_]*$/;

type Fields = Readonly<Record<keyof AuditFilter, string>>;
type Errors = Partial<Record<keyof AuditFilter, string>>;

/** The checks that the API makes, made first, so a person is told in words and no request is sent for a filter that cannot match. */
function validate(fields: Fields): Readonly<{ filter: AuditFilter; errors: Errors }> {
  const errors: Errors = {};
  const text = (key: keyof AuditFilter, pattern: RegExp, message: string) => {
    const value = fields[key].trim();
    if (value !== "" && !pattern.test(value)) errors[key] = message;
  };
  text("actorId", RECORD_ID, "Use the id of the actor: letters, digits and . _ : -.");
  text(
    "action",
    ACTION,
    "Use an action such as payment_request.cancelled, or a family such as payment_request.",
  );
  text("targetType", RECORD_TYPE, "Use a record type such as payment_request.");
  text("targetId", RECORD_ID, "Use the id of the record: letters, digits and . _ : -.");
  const from = fields.from === "" ? "" : (utcInputToIso(fields.from) ?? "");
  const to = fields.to === "" ? "" : (utcInputToIso(fields.to) ?? "");
  if (fields.from !== "" && from === "") errors.from = "Enter a start date and time (UTC).";
  if (fields.to !== "" && to === "") errors.to = "Enter an end date and time (UTC).";
  if (from !== "" && to !== "" && Date.parse(to) <= Date.parse(from)) {
    errors.to = "The end must be after the start.";
  }
  return {
    filter: {
      actorId: fields.actorId.trim(),
      action: fields.action.trim(),
      targetType: fields.targetType.trim(),
      targetId: fields.targetId.trim(),
      from,
      to,
    },
    errors,
  };
}

type AuditRow = NonNullable<
  ReturnType<typeof useAuditEvents>["data"]
>["pages"][number]["items"][number];
type AuditView = Readonly<{ row: AuditRow }>;

// The column list is a module constant: `DataTable` draws a cell with `flexRender`, which treats a cell function as a component.
const auditColumn = createColumnHelper<AuditView>();
const auditColumns = wrapCells([
  auditColumn.display({
    id: "when",
    header: "When",
    cell: ({ row }) => formatInstant(row.original.row.occurredAt),
  }),
  auditColumn.display({
    id: "actor",
    header: "Actor",
    cell: ({ row }) => {
      const { actor } = row.original.row;
      return (
        <>
          {auditActorKindLabels[actor.kind]}
          {actor.displayName === null ? null : <> {actor.displayName}</>}{" "}
          <code title={actor.id}>{actor.id.slice(0, 8)}</code>
        </>
      );
    },
  }),
  auditColumn.display({
    id: "action",
    header: "Action",
    cell: ({ row }) => <code>{row.original.row.action}</code>,
  }),
  auditColumn.display({
    id: "record",
    header: "Record",
    cell: ({ row }) => (
      <>
        <RecordLink type={row.original.row.target.type} id={row.original.row.target.id} />
        <span className="text-xs text-muted-foreground">
          {" "}
          <code>{row.original.row.target.type}</code>
        </span>
      </>
    ),
  }),
  auditColumn.display({
    id: "reason",
    header: "Reason",
    cell: ({ row }) => {
      const event = row.original.row;
      const reasonLabel = event.reasonCode
        ? controlReasonLabels[event.reasonCode as StaffOperationsControlReason]
        : undefined;
      const resolutionLabel = event.resolutionCode
        ? resolutionLabels[event.resolutionCode as keyof typeof resolutionLabels]
        : undefined;
      return (
        <>
          {event.reason}
          {event.reasonCode === null ? null : (
            <span className="block text-xs text-muted-foreground">
              Reason code <code>{event.reasonCode}</code>
              {reasonLabel === undefined ? null : <> ({reasonLabel})</>}
            </span>
          )}
          {event.resolutionCode === null ? null : (
            <span className="block text-xs text-muted-foreground">
              Decision <code>{event.resolutionCode}</code>
              {resolutionLabel === undefined ? null : <> ({resolutionLabel})</>}
            </span>
          )}
          {event.tab === null ? null : (
            <span className="block text-xs text-muted-foreground">
              Tab <code>{event.tab}</code>
            </span>
          )}
          {event.reason === null &&
          event.reasonCode === null &&
          event.resolutionCode === null &&
          event.tab === null
            ? "—"
            : null}
        </>
      );
    },
  }),
  auditColumn.display({
    id: "correlation",
    header: "Correlation id",
    cell: ({ row }) => <code>{row.original.row.correlationId}</code>,
  }),
]);

export function AuditPage() {
  useWideLayout();
  const { capabilities, isLoading } = useDiagnosticsCapabilities();
  const [params] = useSearchParams();
  const initial = params.get("action") ?? "";
  const [fields, setFields] = useState<Fields>({ ...emptyAuditFilter, action: initial });
  const [applied, setApplied] = useState<AuditFilter>({
    ...emptyAuditFilter,
    action: ACTION.test(initial) ? initial : "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const events = useAuditEvents(applied, capabilities.audit);

  if (isLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.audit) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Only a supervisor or an administrator reads the audit trail."
      />
    );
  }

  const set = (key: keyof AuditFilter) => (value: string) =>
    setFields((current) => ({ ...current, [key]: value }));
  const familyValue = auditFamilies.some((family) => family.value === fields.action)
    ? fields.action
    : "";
  const rows = events.data?.pages.flatMap((page) => page.items) ?? [];
  const views: AuditView[] = rows.map((row) => ({ row }));

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-6" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Lafayette operations"
        title="Audit trail"
        titleId="page-title"
        description="Who did what to which record, when and why. Newest first. The trail holds no message text and no secret. Staff reasons are internal."
        actions={
          <AreaLinks
            links={[
              { to: "/operations/diagnostics", label: "Diagnostics" },
              { to: "/operations", label: "Jobs and queues" },
              { to: runbookPath(runbooks.providerOutage), label: "Runbooks" },
            ]}
          />
        }
      />

      <form
        noValidate
        aria-label="Filter the audit trail"
        onSubmit={(event) => {
          event.preventDefault();
          const checked = validate(fields);
          setErrors(checked.errors);
          if (Object.keys(checked.errors).length === 0) setApplied(checked.filter);
        }}
      >
        <Card>
          <CardContent className="gap-4 pt-4 sm:pt-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <SelectField
                id="audit-family"
                label="Kind of action"
                value={familyValue}
                placeholder="Any action"
                onChange={(value) => set("action")(value)}
                options={auditFamilies.map((family) => ({
                  value: family.value,
                  label: family.label,
                }))}
              />
              <TextField
                id="audit-action"
                label="Action or family"
                value={fields.action}
                error={errors.action}
                hint="For example payment_request.cancelled, or payment_request for all of them."
                onChange={set("action")}
              />
              <TextField
                id="audit-actor"
                label="Actor id"
                value={fields.actorId}
                error={errors.actorId}
                onChange={set("actorId")}
              />
              <TextField
                id="audit-target-type"
                label="Record type"
                value={fields.targetType}
                error={errors.targetType}
                hint="For example payment_request."
                onChange={set("targetType")}
              />
              <TextField
                id="audit-target-id"
                label="Record id"
                value={fields.targetId}
                error={errors.targetId}
                onChange={set("targetId")}
              />
              <TextField
                id="audit-from"
                label="From (UTC)"
                type="datetime-local"
                value={fields.from}
                error={errors.from}
                onChange={set("from")}
              />
              <TextField
                id="audit-to"
                label="Until (UTC)"
                type="datetime-local"
                value={fields.to}
                error={errors.to}
                onChange={set("to")}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit">
                <Icon name="search" />
                Search
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setFields(emptyAuditFilter);
                  setApplied(emptyAuditFilter);
                  setErrors({});
                }}
              >
                Clear the filter
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>

      <section className="grid gap-3" aria-labelledby="audit-results">
        <h2 id="audit-results" className="m-0 font-serif text-[26px] leading-8 font-medium">
          Events
        </h2>
        {events.isPending ? (
          <AsyncState kind="loading" title="Loading the events" message="Waiting for the answer." />
        ) : events.isError && events.data === undefined ? (
          <AsyncState
            kind="error"
            title="The audit trail could not be read"
            message={describeDiagnosticsError(events.error).message}
            onRetry={() => void events.refetch()}
          />
        ) : rows.length === 0 ? (
          <AsyncState kind="empty" title="No event" message="No audit event matches this filter." />
        ) : (
          <>
            <p className="m-0 text-sm leading-6 text-muted-foreground" role="status">
              {rows.length} {rows.length === 1 ? "event" : "events"} shown, newest first.
            </p>
            <DataTable<AuditView>
              caption="Audit events"
              data={views}
              getRowId={(view) => view.row.id}
              rowAttributes={(view) => ({ "data-sequence": String(view.row.sequence) })}
              columns={auditColumns}
              banded="strong"
            />
            {events.hasNextPage ? (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={events.isFetchingNextPage}
                  onClick={() => void events.fetchNextPage()}
                >
                  {events.isFetchingNextPage ? "Loading…" : "Load more events"}
                </Button>
              </div>
            ) : (
              <p className="m-0 text-sm leading-6 text-muted-foreground">
                That is every event for this filter.
              </p>
            )}
          </>
        )}
      </section>
    </section>
  );
}
